/**
 * Language Context for Universe App
 * Ticket #79: Set preferred language via Profile setting
 *
 * Provides app-wide language state and translation function.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from '../translations';

const LanguageContext = createContext();

// Map internal language values to translation keys
const languageMap = {
  'Deutsch': 'de',
  'Englisch': 'en',
  'de': 'de',
  'en': 'en',
};

export const LanguageProvider = ({ children, initialLanguage = 'Deutsch' }) => {
  const [language, setLanguage] = useState(() => {
    // Try to get language from localStorage first
    const saved = localStorage.getItem('preferredLanguage');
    return saved || initialLanguage;
  });

  // Get the translation key (de/en) from the language setting
  const langKey = languageMap[language] || 'de';

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', language);
  }, [language]);

  // Translation function - gets nested keys like 'menu.logout'
  const t = useCallback((key, fallback = '') => {
    const keys = key.split('.');
    let value = translations[langKey];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Key not found, try German as fallback
        let fallbackValue = translations['de'];
        for (const fk of keys) {
          if (fallbackValue && typeof fallbackValue === 'object' && fk in fallbackValue) {
            fallbackValue = fallbackValue[fk];
          } else {
            return fallback || key; // Return the key itself or custom fallback
          }
        }
        return fallbackValue;
      }
    }

    return typeof value === 'string' ? value : (fallback || key);
  }, [langKey]);

  const value = {
    language,           // 'Deutsch' or 'Englisch'
    langKey,            // 'de' or 'en'
    setLanguage,        // Function to change language
    t,                  // Translation function
    isGerman: langKey === 'de',
    isEnglish: langKey === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
